import moment from 'moment';

import { DEFAULT_DATE_FORMAT } from 'constants/Common';
import { formatPrice } from 'util/string';

export function fmtDate(text) {
  return text ? moment(text).format(DEFAULT_DATE_FORMAT) : '';
}

export function fmtAmount(text) {
  if (text == null) return '';

  const num = parseFloat(text);

  return Number.isNaN(num) ? '' : formatPrice(num);
}

// Snapshot date for Mẫu 01/BK-HTK (year-end on-hand inventory, TT 18/2026/TT-BTC).
export const STOCKTAKE_AS_OF = '2025-12-31';

export const STOCKTAKE_APPLICABILITY_MESSAGE =
  'Chỉ áp dụng cho HKD có doanh thu ≥ 3 tỷ VND năm 2025 hoặc áp dụng thuế TNCN theo biểu luỹ tiến.';

// Mirrors the backend `is_stocktake_declaration_applicable` gate so the FE only
// surfaces the 01/BK-HTK book for tenants the endpoint would serve (else 403).
export function isStocktakeDeclarationApplicable(policy) {
  const bracket = policy?.revenue_bracket;

  if (bracket === '3b_to_50b' || bracket === 'over_50b') return true;

  const isFullBooks = Boolean(bracket) && bracket !== 'under_1b';

  return isFullBooks && policy?.pit_method === 'income_based';
}

export const REVENUE_NOTIFICATION_APPLICABILITY_MESSAGE =
  'Chỉ áp dụng cho HKD có doanh thu năm từ 01 tỷ đồng trở xuống.';

export const NO_DECLARATIONS_MESSAGE = 'Không có bảng kê, tờ khai áp dụng cho hộ kinh doanh của bạn.';

// Mirrors the backend `is_revenue_notification_applicable` gate (01/TKN-CNKD —
// the under-1B annual revenue notification, TT 50/2026).
export function isRevenueNotificationApplicable(policy) {
  return policy?.revenue_bracket === 'under_1b';
}

export const CNKD_FILING_APPLICABILITY_MESSAGE = 'Chỉ áp dụng cho HKD kê khai (doanh thu năm trên 01 tỷ đồng).';

// Mirrors the backend `is_cnkd_filing_applicable` gate (01/CNKD — the periodic
// GTGT/TNCN return for kê khai HKDs, i.e. any full-books bracket, TT 18/2026).
export function isCnkdFilingApplicable(policy) {
  const bracket = policy?.revenue_bracket;

  return Boolean(bracket) && bracket !== 'under_1b';
}

export const PIT_FINALIZATION_APPLICABILITY_MESSAGE =
  'Chỉ áp dụng cho HKD nộp thuế TNCN theo phương pháp trên thu nhập (kê khai).';

// Mirrors the backend `is_pit_finalization_applicable` gate (02/CNKD-TNCN-QTT —
// the annual PIT finalization for income-based HKDs, TT 18/2026).
export function isPitFinalizationApplicable(policy) {
  return policy?.pit_method === 'income_based';
}

// Mirrors the backend applicability gate for 01/BK-STK (bank account list —
// available to all HKD tenants with books enabled, no revenue-bracket gate).
export function isBkStkApplicable(policy) {
  return !!policy?.books_enabled;
}

// 01/TB-ĐĐKD (business-location notice) applies to all HKD tenants with books
// enabled — same gate as 01/BK-STK, aliased for clarity in card declarations.
export const isTbDdkdApplicable = isBkStkApplicable;

// The "Bảng kê, tờ khai" surface shows when at least one declaration applies.
// Gates can overlap — an income-based ≥1B tenant sees 01/CNKD, 02/QTT and
// 01/BK-HTK together — so each card filters itself by its own predicate.
export function hasApplicableDeclarations(policy) {
  return (
    isStocktakeDeclarationApplicable(policy) ||
    isRevenueNotificationApplicable(policy) ||
    isCnkdFilingApplicable(policy) ||
    isPitFinalizationApplicable(policy) ||
    isBkStkApplicable(policy) ||
    isTbDdkdApplicable(policy)
  );
}
